import { apiInstance, appInstance } from '@/helpers/axios'

export type {
	BaseResponse,
	Controller,
	HpRequest,
	HpRequestOptional,
	HpRequestPostPut,
	HpService,
} from './serviceTypes'

import type {
	BaseResponse,
	HpRequest,
	HpRequestOptional,
	HpRequestPostPut,
} from './serviceTypes'

export interface IHttpClient {
	request<T>(config: any): BaseResponse<T>
}

export class Service {
	// Resolve the http client lazily. service.ts and @/helpers/axios form an
	// import cycle (axios → interceptors → stores → services → service.ts → axios).
	// Reading apiInstance/appInstance at module-eval time hits the temporal dead
	// zone whenever axios is the first module in that cycle to evaluate
	// ("Cannot access 'apiInstance' before initialization"). Deferring the read to
	// the first request — long after all modules are initialized — breaks the TDZ.
	constructor(private httpClientFactory: () => IHttpClient) {}

	http() {
		return this.httpClientFactory()
	}

	get<T>(request: HpRequest) {
		return this.http().request<T>({
			method: 'get',
			...request,
		})
	}
	post<T>(request: HpRequest) {
		return this.http().request<T>({
			method: 'post',
			...request,
		})
	}
	put<T>(request: HpRequest) {
		return this.http().request<T>({
			method: 'put',
			...request,
		})
	}
	delete<T>(request: Pick<HpRequest, 'url'>) {
		return this.http().request<T>({
			method: 'delete',
			...request,
		})
	}
	options<T>({ url }: Pick<HpRequest, 'url'>) {
		return this.http().request<T>({
			method: 'options',
			url,
		})
	}
}

export const apiService = new Service(() => apiInstance)
export const appService = new Service(() => appInstance)

export const getController = <Response, PostInput = any, PutInput = any>(
	url: string,
	service: Service = apiService
) => {
	return {
		index: (data?: HpRequestOptional) =>
			service.get<Api.Collection<Response>>({ url, ...data }),
		show: (id: number, data?: HpRequestOptional) =>
			service.get<Response>({ url: `${url}/${id}`, ...data }),
		create: (data: HpRequestPostPut<PostInput>) =>
			service.post<Response>({ url, ...data }),
		update: (id: number, data: HpRequestPostPut<PutInput>) =>
			service.put<Response>({ url: `${url}/${id}`, ...data }),
		delete: (id: number, data?: HpRequestOptional) =>
			service.delete<unknown>({ url: `${url}/${id}`, ...data }),
		attach: (
			id: number,
			target: string,
			targetId: number,
			data?: HpRequestOptional
		) =>
			service.post<unknown>({
				url: `${url}/${id}/${target}/${targetId}`,
				...data,
			}),
		detach: (
			id: number,
			target: string,
			targetId: number,
			module?: string,
			data?: HpRequestOptional
		) =>
			service.delete<unknown>({
				url: `${url}/${id}/${target}/${targetId}${module ? `/${module}` : ''}`,
				...data,
			}),
	}
}
